import type { CountryCode } from 'libphonenumber-js';

import { useCallback } from 'react';

import { useAuthStore } from './useAuthStore';
import { useSignUp } from '@goodneighbor/api-client';

import { parseToInternationalPhoneNumber } from '../utils/parseToInternationalPhoneNumber';

type UseSignUpReturn = ReturnType<typeof useSignUp>;
type MutationParams = Parameters<UseSignUpReturn['mutateAsync']>[0]['data'];

type ExtraSignUpOtpData = Omit<
  MutationParams,
  'identifier' | 'identifierType' | 'credential' | 'confirmCredential'
>;

type SignUpOtpData = {
  phone: string;
  profile: {
    phone: string;
    firstName: string;
    lastName: string;
  };
  countryCode?: CountryCode;
} & ExtraSignUpOtpData;

/**
 * @returns function to be used to sign up with otp support. If countryCode is not set, US is used as a default
 */
const useSignUpOtpWithPersistence = () => {
  const { mutateAsync, ...mutationMetadata } = useSignUp();
  const setTokens = useAuthStore((state) => state.setTokens);

  const handleSignUpOtpWithPersistence = useCallback(
    async ({ phone, countryCode, ...rest }: SignUpOtpData) => {
      const internationalPhoneNumber = parseToInternationalPhoneNumber(
        phone,
        countryCode
      );

      const { data } = await mutateAsync({
        data: {
          identifier: internationalPhoneNumber,
          ...rest
        }
      });

      setTokens(data.tokens);

      return data;
    },
    [mutateAsync, setTokens]
  );

  return { handleSignUpOtpWithPersistence, ...mutationMetadata };
};

export type { ExtraSignUpOtpData, SignUpOtpData };
export { useSignUpOtpWithPersistence };
